import React from "react";

import {
  faCheck,
  faQuestion,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid } from "@material-ui/core";
import { useSelector } from "react-redux";
import styled from "styled-components/macro";

import { AdminLink } from "components/AdminLink/AdminLink";
import AuditLogLink from "components/AuditLog/AuditLogLink";
import { Chip, ChipBag, AngusVerifiedEIDChip } from "components/Chip";
import { Row } from "components/Layout";

import { AuditLogTypes } from "constants/auditLog";
import { Species } from "constants/species";

import { formatWeightKg } from "lib";

import { formatUTCToLocalDateTimeString } from "lib/timeFormats";

import {
  getConsignmentById,
  getPropertyById,
  getSaleLotById,
  getSaleLotsBySale,
  getSaleyardScanSaleLotById,
  getScans,
  getWeighLotById,
  getWeighLotScanById,
} from "selectors";

const Scan = styled(Grid)(
  ({ theme }) => `
  border: 1px solid ${theme.colors.gray78};
  background-color: ${theme.colors.white};
`,
);

const StyledBooleanIcon = styled(FontAwesomeIcon)(
  ({ status, theme }) => `
color: ${status === true ? theme.colors.success : theme.colors.error};
`,
);

const ScanHeader = styled.div`
  font-weight: bold;
  ${({ right }) => right && `margin-left: auto;`}
`;

const BooleanIcon = ({ status }) => {
  let icon;
  switch (status) {
    case true:
      icon = faCheck;
      break;
    case false:
      icon = faTimes;
      break;
    default:
      icon = faQuestion;
      break;
  }
  return <StyledBooleanIcon icon={icon} status={status} />;
};

interface Props {
  EID: string;
  saleLotId?: string;
  largeView?: boolean;
}

export function EIDWithNLISDataItem({ EID, saleLotId, largeView }: Props) {
  const allScans = useSelector(getScans);
  const rawScan = allScans[EID];

  const weighLotScan =
    useSelector(getWeighLotScanById(rawScan?.weigh_lot_scan_id)) || {};
  const weighLot = useSelector(getWeighLotById(weighLotScan.weighLotId));

  const preferredSaleLotId = saleLotId || rawScan?.sale_lot_id;

  const saleLotFromId = useSelector(getSaleLotById(preferredSaleLotId));
  const saleLotFromSaleyard = useSelector(
    getSaleyardScanSaleLotById(preferredSaleLotId),
  );
  const saleLot: any = saleLotFromId || saleLotFromSaleyard || {}; // eslint-disable-line @typescript-eslint/no-explicit-any
  const consignment =
    useSelector(
      getConsignmentById(saleLot.consignment_id || saleLot.consignmentId),
    ) || {};
  const vendorProperty =
    useSelector(getPropertyById(consignment.vendor_property_id)) || {};
  const allSaleLotsBySale = useSelector(getSaleLotsBySale) as any; // eslint-disable-line @typescript-eslint/no-explicit-any

  if (!rawScan) {
    return null;
  }

  const {
    animal: { nlis_id: nlisId },
    current_pic: currentPic,
    ERP_status: erpStatus,
    EU_status: euStatus,
    id,
    lifetime_traceability: lifetimeTraceability,
    time_weighed: timeWeighed,
    total_mass_grams: totalMassGrams,
  } = rawScan;

  const isCattle = saleLot.sale_round?.species === Species.CATTLE;
  const vendorPIC = vendorProperty.PIC;

  const lifetimeTraceabilityStatus = lifetimeTraceability
    ? lifetimeTraceability === "Y"
    : null;

  const currentPicStatus = currentPic ? currentPic === vendorPIC : null;

  let vendorName: string | undefined;
  let buyerName: string | undefined;
  let buyerPIC: string | undefined;
  let penName: string | undefined;

  if (largeView) {
    const extendedSaleLot = allSaleLotsBySale.find(
      sl => sl.id === preferredSaleLotId,
    );
    if (extendedSaleLot) {
      vendorName = extendedSaleLot.vendor_name;
      buyerName = extendedSaleLot.buyer_name;
      buyerPIC = extendedSaleLot.destinationProperty?.PIC;
      ({ penName } = extendedSaleLot);
    }
  }

  return (
    <Scan item>
      <Row justifyAround>
        <AuditLogLink
          auditLogType={AuditLogTypes.SCAN}
          dataId={id}
          returnTo={window.location.hash}
        />
        <ScanHeader>{EID}</ScanHeader>
        <AdminLink model="scan" cbid={id} />

        <ScanHeader right>{nlisId}</ScanHeader>
      </Row>
      <ChipBag>
        <Chip tooltip="ERP status returned from NLIS">ERP: {erpStatus}</Chip>

        <Chip tooltip="Current device PIC registered with NLIS.">
          {`Device PIC ${currentPic || "unknown"}`}{" "}
          <BooleanIcon status={currentPicStatus} />
        </Chip>

        <Chip tooltip="Lifetime traceability status">
          LT <BooleanIcon status={lifetimeTraceabilityStatus} />
        </Chip>
        {isCattle && <Chip tooltip="EU status">EU: {euStatus}</Chip>}
        {vendorName && <Chip>Vendor: {vendorName}</Chip>}
        {vendorName && vendorPIC && <Chip>Vendor PIC: {vendorPIC}</Chip>}
        {penName && <Chip>Pen: {penName}</Chip>}
        {buyerName && <Chip>Buyer: {buyerName}</Chip>}
        {buyerPIC && <Chip>Buyer PIC: {buyerPIC}</Chip>}
        {totalMassGrams && (
          <Chip>
            Weighed: {formatWeightKg(totalMassGrams, true)}{" "}
            {timeWeighed && `@ ${formatUTCToLocalDateTimeString(timeWeighed)}`}
          </Chip>
        )}
        {weighLot && (
          <Chip>
            Weigh Lot: {weighLot.scaleName} {weighLot.lotNumber}
          </Chip>
        )}
        <AngusVerifiedEIDChip EID={EID} />
      </ChipBag>
    </Scan>
  );
}

export default EIDWithNLISDataItem;
